import { computed, shallowRef } from 'vue'
import { promiseTimeout, until, useEventBus } from '@vueuse/core'
import { isAxiosError } from 'axios'
import { useExecutorApi } from '@/composables/executor/useExecutorApi'
import { useWorkflow } from '@/composables/workflow/useWorkflow'
import useWidgetViewManager from '@/composables/widget-view-manager/useWidgetViewManager'
import useEmbed from '@/embed/code'
import type { CustomPageLayout } from '@/views/apps/_customPage/types'
import type { Message, WorkflowStatus } from '../websocket/frames'
import { inflateLayout } from '../layout'
import { firstMessageViewState, messageViewState, nextMessageViewState } from '../view-state'
import { NOTIFICATIONS_UNAVAILABLE, PAGE_UNAVAILABLE, SESSION_EXPIRED, WORKFLOW_CANCELLED } from '../types'
import { UPLOAD_SCANNED, reportUploadScansMissed, reportUploadScansUnavailable, resetUploadScans } from '../upload-scanned'
import type { SessionContext, SessionStrategy, ViewRef, WorkflowEndReason } from '../types'
import type { ViewState } from '../view-state'
import { RECONNECT_DELAYS_MS, TERMINAL_STATES, openNotificationStream } from './notifications'
import type { WorkflowLifecycleEvent, WorkflowLifecycleState } from './notifications'

type CustomPage = { message: Message, layout: CustomPageLayout }

const FIRST_VIEW_TIMEOUT_MS = 600 * 1000

const WORKFLOW_STATUS: Record<WorkflowLifecycleState, WorkflowStatus> = {
    STARTED: 'RUNNING',
    CUSTOM_PAGE_RENDERED: 'RUNNING',
    CUSTOM_PAGE_UPLOAD_SCANNED: 'RUNNING',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
    CANCELLED: 'CANCELLED',
    EXPIRED: 'TIMEOUT',
}

const toMessage = (event: WorkflowLifecycleEvent, customPage?: { id: string, layout: CustomPageLayout }) => {
    const { decodedToken } = useWorkflow()

    return {
        taskId: customPage?.id ?? event.workflowId,
        taskName: 'CUSTOM_PAGE_2',
        taskType: 'CUSTOM_PAGE_2',
        taskStatus: 'STARTED',
        partyId: decodedToken.value?.partyId ?? '',
        workspaceId: event.workspaceId,
        workflowId: event.workflowId,
        workflowDefinitionId: decodedToken.value?.workflowDefinitionId ?? '',
        layoutDescriptor: JSON.stringify(customPage?.layout ?? {}),
        workflowStatus: WORKFLOW_STATUS[event.state],
        when: event.occurredAt,
    } as Message
}

const customPageUrl = (message: Message) => `workflows/${message.workflowId}/custom-pages/${message.taskId}`

const requestLayout = async (workflowId: string, layoutId: string, locale?: string): Promise<CustomPageLayout> => {
    const url = `workflows/${workflowId}/custom-pages/${layoutId}`

    if (!locale) {
        return (await useExecutorApi().get<CustomPageLayout>(url)).data
    }

    const { data } = await useExecutorApi().post<string>(`${url}/render`, null, { params: { locale } })

    return inflateLayout(data)
}

const fetchLayout = async (workflowId: string, layoutId: string, locale?: string, attempt = 0): Promise<CustomPageLayout> => {
    try {
        return await requestLayout(workflowId, layoutId, locale)
    } catch (error) {
        if (attempt === RECONNECT_DELAYS_MS.length || (isAxiosError(error) && error.response?.status === 401)) {
            throw error
        }

        await promiseTimeout(RECONNECT_DELAYS_MS[attempt])

        return fetchLayout(workflowId, layoutId, locale, attempt + 1)
    }
}

export const createEngineV2Strategy = (): SessionStrategy => {
    let stream: ReturnType<typeof openNotificationStream> | undefined
    let latestPageVersion = -1
    let run = 0
    let endHandler: ((reason: WorkflowEndReason) => void) | undefined
    let selectedLocale: string | undefined

    const customPage = shallowRef<CustomPage>()
    const ended = shallowRef<WorkflowLifecycleEvent>()
    const unavailable = shallowRef<string>()
    const submitted = new Set<string>()

    const throwIfUnavailable = () => {
        if (unavailable.value) {
            throw new Error(unavailable.value)
        }
    }

    const showCustomPage = async (event: WorkflowLifecycleEvent) => {
        const layoutId = event.payload?.layoutId

        if (!layoutId || submitted.has(layoutId) || event.version <= latestPageVersion) {
            return
        }

        latestPageVersion = event.version
        const pageRun = run

        let layout: CustomPageLayout

        try {
            layout = await fetchLayout(event.workflowId, layoutId, selectedLocale)
        } catch (error) {
            if (pageRun !== run || event.version !== latestPageVersion || ended.value) {
                return
            }

            if (isAxiosError(error) && error.response?.status === 401) {
                return expire()
            }

            unavailable.value = PAGE_UNAVAILABLE
            return
        }

        if (pageRun === run && event.version === latestPageVersion && !ended.value) {
            customPage.value = { message: toMessage(event, { id: layoutId, layout }), layout }
        }
    }

    const end = (event: WorkflowLifecycleEvent) => {
        ended.value = event
        stream?.close()

        if (event.state === 'CANCELLED') {
            endHandler?.('cancelled')
        }

        if (event.state === 'EXPIRED') {
            endHandler?.('timeout')
        }
    }

    const onEvent = (event: WorkflowLifecycleEvent) => {
        if (ended.value) {
            return
        }

        if (TERMINAL_STATES.includes(event.state)) {
            return end(event)
        }

        if (event.state === 'CUSTOM_PAGE_UPLOAD_SCANNED' && event.payload?.scanResultStatus) {
            const { objectKey, isMalicious = false, scanResultStatus } = event.payload

            useEventBus(UPLOAD_SCANNED).emit({ objectKey, isMalicious, scanResultStatus })
        }

        if (event.state === 'CUSTOM_PAGE_RENDERED') {
            showCustomPage(event)
        }
    }

    const expire = () => {
        const { workflowId, decodedToken } = useWorkflow()

        onEvent({
            workspaceId: decodedToken.value?.workspaceId ?? '',
            workflowId: workflowId.value ?? '',
            state: 'EXPIRED',
            version: 0,
            occurredAt: new Date().toISOString(),
        })
    }

    const resetRun = () => {
        run++
        resetUploadScans()
        stream?.close()
        stream = undefined
        latestPageVersion = -1
        endHandler = undefined
        selectedLocale = undefined
        customPage.value = undefined
        ended.value = undefined
        unavailable.value = undefined
        submitted.clear()

        useWidgetViewManager().resetView()
    }

    return {
        kind: 'engine-v2',

        preConnect: async () => {},

        validateSession: async () => {},

        resolveWorkflow: async () => {},

        ensureConnected: async () => {
            const streamRun = run
            const forThisRun = <A extends unknown[]>(handler: (...args: A) => void) => (...args: A) => {
                if (streamRun === run) {
                    handler(...args)
                }
            }

            stream ??= openNotificationStream({
                onEvent: forThisRun(onEvent),
                onUnauthorized: forThisRun(expire),
                onUnavailable: forThisRun(() => {
                    unavailable.value = NOTIFICATIONS_UNAVAILABLE
                    reportUploadScansUnavailable(NOTIFICATIONS_UNAVAILABLE)
                }),
                onReconnected: forThisRun(reportUploadScansMissed),
            })
        },

        start: async () => {
            try {
                const { data } = await useExecutorApi().post(`/workflows/${useWorkflow().workflowId.value}/run`)

                return data
            } catch (error) {
                if (isAxiosError(error) && error.response?.status === 401) {
                    throw new Error(SESSION_EXPIRED)
                }

                throw error
            }
        },

        awaitFirstView: async (): Promise<ViewState> => {
            await until(computed(() => ended.value || customPage.value || unavailable.value))
                .toBeTruthy({ timeout: FIRST_VIEW_TIMEOUT_MS, throwOnTimeout: true })

            throwIfUnavailable()

            if (ended.value?.state === 'EXPIRED') {
                throw new Error(SESSION_EXPIRED)
            }

            if (ended.value?.state === 'CANCELLED') {
                throw new Error(WORKFLOW_CANCELLED)
            }

            if (ended.value) {
                const state = ended.value.state === 'COMPLETED' ? 'COMPLETED' : 'FAILED'

                return firstMessageViewState(toMessage({ ...ended.value, state }))
            }

            useEmbed().setWorkflowExecutionPhase('running')

            return firstMessageViewState(customPage.value!.message)
        },

        advance: async (_ctx: SessionContext, current: ViewRef): Promise<ViewState> => {
            submitted.add(current.id)

            const finished = computed(() => ended.value?.state === 'COMPLETED' || ended.value?.state === 'FAILED')

            await until(computed(() => finished.value || unavailable.value || (customPage.value && !submitted.has(customPage.value.message.taskId))))
                .toBeTruthy()

            throwIfUnavailable()

            return nextMessageViewState(finished.value ? toMessage(ended.value!) : customPage.value!.message)
        },

        changeLanguage: async (_ctx: SessionContext, lang: string) => {
            const { message } = customPage.value!
            const renderRun = run

            await useExecutorApi().post(`/workflows/${message.workflowId}/resume`, {
                language: lang,
                taskId: message.taskId,
                taskType: message.taskType,
                workflowId: message.workflowId,
            })

            const { data } = await useExecutorApi().post<string>(
                `${customPageUrl(message)}/render`,
                null,
                { params: { locale: lang } },
            )

            const layout = inflateLayout(data)
            const translated = { ...message, layoutDescriptor: JSON.stringify(layout) }

            if (renderRun === run) {
                selectedLocale = lang
                customPage.value = { message: translated, layout }
            }

            return messageViewState(translated)
        },

        resolveLayout: async () => customPage.value!.layout,

        engineApi: useExecutorApi,

        currentTaskEvents: () => customPage.value ? [customPage.value.message] : [],

        latestTaskEvent: () => customPage.value?.message,

        watchWorkflowEnd: (handler: (reason: WorkflowEndReason) => void) => {
            endHandler = handler

            return () => {
                endHandler = undefined
            }
        },

        resetRun,

        disposeSession: () => {
            resetRun()
            useWorkflow().clearToken()
        },
    }
}
